import {
  DEFAULT_CASHBOOK_DEBT_CATEGORIES,
  listSelectableDebtCategories,
  localTodayIso,
  type CashbookDebtCategory
} from '@finvox/shared/cashbook';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CashbookCategorySelect } from '@/components/cashbook/CashbookCategorySelect';
import { DebtOwnerSelect } from '@/components/cashbook/DebtOwnerSelect';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { cn } from '@/lib/cn';

type Props = {
  amount: string;
  categoryKey: string;
  creditor: string;
  date: string;
  memo: string;
  busy: boolean;
  disabled: boolean;
  embedded?: boolean;
  debtOwners: string[];
  customDebtCategories?: CashbookDebtCategory[];
  onCancel?: () => void;
  onAmountChange: (v: string) => void;
  onCategoryChange: (v: string) => void;
  onCreditorChange: (v: string) => void;
  onDateChange: (v: string) => void;
  onMemoChange: (v: string) => void;
  onAdd: () => void;
};

export function CashbookDebtQuickAdd({
  amount,
  categoryKey,
  creditor,
  date,
  memo,
  busy,
  disabled,
  embedded = false,
  debtOwners,
  customDebtCategories = [],
  onCancel,
  onAmountChange,
  onCategoryChange,
  onCreditorChange,
  onDateChange,
  onMemoChange,
  onAdd
}: Props) {
  const { t } = useTranslation();

  const categories = useMemo(
    () =>
      listSelectableDebtCategories(customDebtCategories).map((c) => ({
        value: c.key,
        label: DEFAULT_CASHBOOK_DEBT_CATEGORIES.some((d) => d.key === c.key)
          ? t(`cashbook.debts.categories.${c.labelKey}`)
          : c.key
      })),
    [customDebtCategories, t]
  );

  const canSubmit =
    !disabled && !busy && amount.trim() !== '' && creditor.trim() !== '' && categoryKey.trim() !== '';

  return (
    <div
      className={cn(
        embedded
          ? 'grid grid-cols-1 gap-4 md:grid-cols-2'
          : 'mt-4 flex flex-col gap-4 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-4 sm:flex-row sm:flex-wrap sm:items-end'
      )}
    >
      {!embedded ? (
        <p className="w-full text-sm font-medium text-[var(--fv-text)]">
          {t('cashbook.debts.incurTitle')}
        </p>
      ) : null}
      <div className="w-full">
        <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.amount')}</label>
        <MoneyInput
          className="mt-1 w-full"
          allowEmpty
          value={amount}
          onValueChange={onAmountChange}
          disabled={disabled}
          aria-label={t('cashbook.amount')}
        />
      </div>
      <div className="w-full">
        <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.creditor')}</label>
        <DebtOwnerSelect
          className="mt-1 w-full"
          value={creditor}
          onValueChange={onCreditorChange}
          owners={debtOwners}
          disabled={disabled}
        />
      </div>
      <div className="w-full">
        <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.category')}</label>
        <CashbookCategorySelect
          className="mt-1 w-full"
          value={categoryKey}
          onValueChange={onCategoryChange}
          categories={categories}
          allowCreate
          disabled={disabled}
        />
      </div>
      <div className="w-full">
        <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colDate')}</label>
        <DatePicker
          className="mt-1 w-full"
          value={date}
          onChange={onDateChange}
          disabled={disabled}
          max={localTodayIso()}
        />
      </div>
      <div className={cn('w-full', embedded && 'md:col-span-2')}>
        <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.memo')}</label>
        <Input
          className="mt-1 w-full"
          value={memo}
          onChange={(e) => onMemoChange(e.target.value)}
          disabled={disabled}
          aria-label={t('cashbook.memo')}
        />
      </div>
      {categoryKey === 'loan' ? (
        <p className={cn('w-full text-xs text-[var(--fv-text-muted)]', embedded && 'md:col-span-2')}>
          {t('cashbook.debts.loanCashHint')}
        </p>
      ) : null}
      <div
        className={cn(
          'flex w-full justify-end gap-2',
          embedded ? 'md:col-span-2' : 'sm:w-auto'
        )}
      >
        {embedded && onCancel ? (
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
        ) : null}
        <Button
          className={embedded ? '' : 'sm:w-auto'}
          fullWidth={!embedded}
          loading={busy}
          disabled={!canSubmit}
          onClick={onAdd}
        >
          {t('cashbook.debts.incurAdd')}
        </Button>
      </div>
    </div>
  );
}
